import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

// App-wide toast notifications. Pages currently roll their own status banners; this gives
// a single, consistent affordance for transient feedback. Usage:
//
//   const toast = useToast();
//   toast.success("Saved.");
//   toast.error("Could not save. Try again.");
//
// Successes auto-dismiss quickly; errors linger longer and can always be dismissed by
// tap/click. Renders above everything (z-60, above the z-50 modals) and clears the native
// safe-area at the bottom of the screen.
const ToastContext = createContext(null);

// No-op fallback so callers can use the hook unconditionally even if a screen ever
// renders outside the provider.
const NOOP_TOAST = { success: () => {}, error: () => {}, info: () => {} };

export function useToast() {
  return useContext(ToastContext) || NOOP_TOAST;
}

const TONE_STYLES = {
  success: "bg-emerald-600 text-white",
  error: "bg-red-600 text-white",
  info: "bg-gray-800 text-white dark:bg-gray-700",
};

const TONE_DURATION_MS = { success: 4000, info: 5000, error: 8000 };

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone, message) => {
      if (!message) return;
      const id = ++idRef.current;
      setToasts((list) => [...list.slice(-3), { id, tone, message }]);
      const duration = TONE_DURATION_MS[tone] || 5000;
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  const api = useMemo(
    () => ({
      success: (message) => push("success", message),
      error: (message) => push("error", message),
      info: (message) => push("info", message),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 px-4 pointer-events-none"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              role={t.tone === "error" ? "alert" : "status"}
              onClick={() => dismiss(t.id)}
              className={`pointer-events-auto max-w-md w-full sm:w-auto sm:min-w-[16rem] rounded-xl px-4 py-3 text-sm shadow-lg cursor-pointer ${TONE_STYLES[t.tone] || TONE_STYLES.info}`}
            >
              {t.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}
